import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { TrendingUp, ExternalLink, Coins } from "lucide-react";

/**
 * Sovereign Toolkit — affiliate signal sidebar.
 *
 * Pulls from `/api/sovereign-toolkit?vessel=<vesselId>` and renders a compact
 * card list of high-margin programs the operator can promote on their
 * tenant. The Director can hot-edit the underlying JSON manifest at
 * `/app/backend/data/affiliate_scout_log.json` without a redeploy.
 *
 * Mount this on any tenant landing or operative dashboard:
 *   <SovereignToolkit vessel="eigoo_trade" />
 *   <SovereignToolkit vessel="eigoo_sports" />
 *   <SovereignToolkit vessel="eigoo_techops" />
 *
 * Pass no `vessel` to render the entire log (Director's master view).
 */
export default function SovereignToolkit({ vessel = null, title = "Sovereign Toolkit", compact = false }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const url = vessel ? `/sovereign-toolkit?vessel=${vessel}` : "/sovereign-toolkit";
                const res = await api.get(url);
                if (active) setData(res.data);
            } catch {
                if (active) setData({ programs: [], total: 0 });
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [vessel]);

    const vesselMeta = vessel && data?.vessels?.[vessel];

    return (
        <aside
            data-testid={`sovereign-toolkit-${vessel || "all"}`}
            className="glass rounded-2xl p-5 border border-[hsl(40_50%_35%/0.4)]"
        >
            <div className="flex items-baseline justify-between mb-4">
                <div className="flex items-center gap-2">
                    <Coins size={16} className="text-[hsl(45_85%_68%)]" />
                    <div>
                        <div className="font-heading text-lg tracking-tight">{title}</div>
                        {vesselMeta && (
                            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                                {vesselMeta.vertical}
                            </div>
                        )}
                    </div>
                </div>
                {data?.total > 0 && (
                    <span className="text-[10px] uppercase tracking-wider text-[hsl(45_85%_68%)]">
                        {data.total} signal{data.total !== 1 ? "s" : ""}
                    </span>
                )}
            </div>

            {loading ? (
                <div className="text-xs text-muted-foreground py-4 text-center">Reading scout log…</div>
            ) : !data?.programs?.length ? (
                <div className="text-xs text-muted-foreground py-4 text-center" data-testid={`sovereign-toolkit-${vessel || "all"}-empty`}>
                    No programs mapped to this site yet.
                </div>
            ) : (
                <ul className="space-y-2">
                    {data.programs.map((p) => (
                        <li key={p.id} data-testid={`toolkit-program-${p.id}`}>
                            <a
                                href={p.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block p-3 rounded-lg border border-[hsl(40_40%_25%/0.25)] hover:border-[hsl(45_85%_68%/0.5)] hover:bg-[hsl(151_55%_15%)] transition-all group"
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <TrendingUp size={11} className="text-[hsl(45_85%_68%)] shrink-0" />
                                            <span className="font-semibold text-sm tracking-tight truncate">{p.name}</span>
                                            {p.category && !compact && (
                                                <span className="text-[9px] uppercase tracking-wider text-muted-foreground px-1.5 py-0.5 rounded bg-[hsl(151_55%_13%)]">
                                                    {p.category}
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-1 text-[11px] gold-gradient-text font-mono leading-relaxed">
                                            {p.commission}
                                        </div>
                                    </div>
                                    <ExternalLink size={12} className="text-muted-foreground group-hover:text-[hsl(45_85%_68%)] transition-colors shrink-0 mt-1" />
                                </div>
                            </a>
                        </li>
                    ))}
                </ul>
            )}

            <div className="mt-4 pt-3 border-t border-[hsl(40_40%_30%/0.2)] text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                Director-curated · hot-editable
            </div>
        </aside>
    );
}
